import NextLink from 'next/link';

import { Button, Container, SectionText, Tabs } from '@/atoms';
import { VARIANT_MAPS } from '@/atoms/section-text/SectionText';
import { Card, SwiperCustom } from '@/molecules';
import { ApartmentCard, MapCard } from '@/organisms';

import type { FunctionComponent } from 'react';
import type { TypeBuildingCarrouselProps } from './types';

/**
 * BuildingCarrousel
 */
export const BuildingCarrousel: FunctionComponent<
  TypeBuildingCarrouselProps
> = ({
  buildings,
  tabsName,
  title,
  caption,
  desc,
  textColor,
  ...props
}: TypeBuildingCarrouselProps) => (
  <Container {...props}>
    <SectionText
      caption={caption}
      direction="row"
      textAlign="start"
      variant={textColor}
      title={title}
      desc={desc}
    />
    <Tabs tabsName={tabsName}>
      {buildings.map((building: any, index: any) => (
        <div className="mt-10 flex flex-col gap-20" key={index}>
          <div className="flex flex-row gap-2">
            {building.tipology.caption && (
              <NextLink
                href={`#${building.tipology.caption}${index}`}
                passHref
                prefetch={false}
              >
                <Button as="a" variant="outline" size="small">
                  {building.tipology.caption}
                </Button>
              </NextLink>
            )}
            {building.sharedSpaces.caption && (
              <NextLink
                href={`#${building.sharedSpaces.caption}${index}`}
                passHref
                prefetch={false}
              >
                <Button as="a" variant="outline" size="small">
                  {building.sharedSpaces.caption}
                </Button>
              </NextLink>
            )}
            {building.map.caption && (
              <NextLink
                href={`#${building.map.caption}${index}`}
                passHref
                prefetch={false}
              >
                <Button as="a" variant="outline" size="small">
                  {building.map.caption}
                </Button>
              </NextLink>
            )}
          </div>
          <div id={`${building.tipology.caption}${index}`}>
            <SectionText
              caption={building.tipology.caption}
              direction="row"
              textAlign="start"
              variant={textColor}
              title={building.tipology.title}
              desc={building.tipology.desc}
            />

            <SwiperCustom
              scrollbar={true}
              navigation
              slidesPerView={3}
              slidesPerViewMobile={1}
              gap={24}
              slideGrow={true}
            >
              {building.tipology.apartments.map(
                (tipology: any, index: number) => (
                  <ApartmentCard
                    key={index}
                    landing={true}
                    cardOrientation={
                      building.tipology.apartments.length > 1 ? 'column' : 'row'
                    }
                    buttonWidth={
                      building.tipology.apartments.length?.length > 2 ? 100 : 60
                    }
                    className={`h-full ${
                      building.tipology.apartments.length > 1 ? '' : ' h-full'
                    } `}
                    {...tipology}
                  />
                )
              )}
            </SwiperCustom>
          </div>
          <div id={`${building.sharedSpaces.caption}${index}`}>
            <SectionText
              caption={building.sharedSpaces.caption}
              direction="row"
              textAlign="start"
              variant={textColor}
              title={building.sharedSpaces.title}
              desc={building.sharedSpaces.desc}
            />
            <SwiperCustom
              gap={20}
              navigation
              scrollbar
              slidesPerView={3}
              slidesPerViewMobile={1.3}
            >
              {building.sharedSpaces.cards.map(
                (
                  {
                    caption,
                    desc,
                    image,
                    title,
                  }: {
                    caption: string;
                    desc: string;
                    image: any;
                    title: string;
                  },
                  index: number
                ) => (
                  <Card
                    multimediaProps={{ image: image }}
                    key={index}
                    className={`h-full ${VARIANT_MAPS[textColor]}  md:ml-0 ${
                      index === 0 ? 'ml-5' : ''
                    } ${
                      index === building.sharedSpaces.cards.length - 1
                        ? 'mr-5'
                        : ''
                    }`}
                  >
                    {caption && (
                      <span className="text-caption text-caption--small mb-2">
                        {caption}
                      </span>
                    )}

                    <h4 className="text-heading text-heading--h4">{title}</h4>

                    {desc && (
                      <p className="text-body text-body--primary mt-4">
                        {desc}
                      </p>
                    )}
                  </Card>
                )
              )}
            </SwiperCustom>
          </div>
          <div id={`${building.map.caption}${index}`}>
            <SectionText
              caption={building.map.caption}
              direction="row"
              textAlign="start"
              variant={textColor}
              title={building.map.title}
              desc={building.map.desc}
            />

            <MapCard orientation="row" {...building.map.mapCard} />
          </div>
        </div>
      ))}
    </Tabs>
  </Container>
);

BuildingCarrousel.displayName = 'BuildingCarrousel';
